/**
 * Anivaze v4 — the ANIMATOR'S CRAFT library.
 *
 * The classical principles of animation (Disney's 12 — squash & stretch,
 * anticipation, follow-through & overlap, arcs, slow-in/slow-out, secondary
 * action, timing, exaggeration) implemented as PURE functions of time, so every
 * video gains a master animator's motion grammar at $0/frame (CLAUDE.md §1.1/1.3).
 * Everything is deterministic and unit-testable; nothing here touches state.
 *
 * Vocabulary (used across actors, entrances, and film cuts):
 *   settle()        — enter with a spring overshoot, then rest (slow-out + life)
 *   anticipate()    — pull BACK slightly before launching forward
 *   arcPoint()      — travel between two points along an arc, never a straight line
 *   squashStretch() — velocity-driven, volume-preserving deformation
 *   lagged()        — a delayed clock for follow-through / overlapping action
 *   breathe()/sway()— idle life so held poses never read as frozen
 *   punch()         — a decaying accent hit on a payoff keyframe
 */

const clamp01 = (x: number): number => (x < 0 ? 0 : x > 1 ? 1 : x);
const TAU = Math.PI * 2;

/* ── slow in / slow out ────────────────────────────────────────────────────── */

export const easeOutQuint = (t: number): number => 1 - Math.pow(1 - clamp01(t), 5);
export const easeInCubic = (t: number): number => { const x = clamp01(t); return x * x * x; };
export const easeInOutCubic = (t: number): number => {
  const x = clamp01(t);
  return x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2;
};

/* ── settle: the entrance with life ────────────────────────────────────────── */

/**
 * Under-damped spring from 0 → 1: rises fast, overshoots (≈1.06 at bounce 0.3),
 * and settles. The single most important upgrade over a linear fade — things
 * ARRIVE instead of appearing. `bounce` 0 = critically damped (no overshoot).
 */
export function settle(t: number, dur = 0.6, bounce = 0.3): number {
  if (t <= 0) return 0;
  const x = Math.min(1, t / Math.max(0.001, dur));
  const decay = Math.exp(-6 * x);
  const osc = Math.cos(TAU * 1.25 * x);
  return 1 - decay * (osc * (0.5 + bounce) + (0.5 - bounce));
}

/** Entrance scale: 0.92 → overshoot ≈1.02 → 1. Pair with opacity = settle(). */
export function settleScale(t: number, dur = 0.6, bounce = 0.3): number {
  return 0.92 + 0.08 * settle(t, dur, bounce) + 0.045 * overshootOnly(t, dur, bounce);
}

/** Just the overshoot component (signed, decays to 0) — for additive accents. */
export function overshootOnly(t: number, dur = 0.6, bounce = 0.3): number {
  if (t <= 0) return 0;
  const x = Math.min(1.5, t / Math.max(0.001, dur));
  return Math.sin(Math.PI * Math.min(1, x)) * Math.exp(-3.2 * x) * bounce;
}

/* ── anticipation ──────────────────────────────────────────────────────────── */

/**
 * 0 → dips to ≈ -backAmt (the wind-up) → then launches to 1. Use for anything
 * that MOVES with intent: a character about to jump, a card about to fly in.
 */
export function anticipate(t: number, dur = 0.7, backFrac = 0.28, backAmt = 0.12): number {
  if (t <= 0) return 0;
  const x = Math.min(1, t / Math.max(0.001, dur));
  if (x < backFrac) {
    const b = x / backFrac;
    return -backAmt * Math.sin(Math.PI * b) * 0.5 * (1 + b);
  }
  const f = (x - backFrac) / (1 - backFrac);
  return (1 + backAmt) * easeOutQuint(f) - backAmt; // launches from the dip, lands exactly at 1
}

/* ── arcs ──────────────────────────────────────────────────────────────────── */

/**
 * Point along a curved path from A to B. `bend` is the sideways bow as a
 * fraction of the travel distance (positive = bows "up"/left of travel).
 * Natural motion travels in arcs — straight lines read as mechanical.
 */
export function arcPoint(
  ax: number, ay: number, bx: number, by: number, p: number, bend = 0.18,
): { x: number; y: number } {
  const u = clamp01(p);
  const mx = ax + (bx - ax) * u;
  const my = ay + (by - ay) * u;
  const dx = bx - ax;
  const dy = by - ay;
  const side = Math.sin(Math.PI * u) * bend;
  return { x: mx - dy * side, y: my + dx * side };
}

/* ── squash & stretch ──────────────────────────────────────────────────────── */

/**
 * Volume-preserving deformation from a normalized vertical velocity
 * (-1 falling fast … +1 rising fast): rising stretches tall, impact squashes
 * wide; area stays ≈ constant so it reads as flesh, not scaling.
 */
export function squashStretch(vNorm: number, amount = 0.18): { sx: number; sy: number } {
  const v = Math.max(-1, Math.min(1, vNorm));
  const sy = 1 + amount * v;
  return { sx: 1 / sy, sy };
}

/* ── follow-through & overlapping action ───────────────────────────────────── */

/** A clock that lags the main action — drive secondary parts (arms, tails,
 *  labels) with `lagged(t, 0.08)` so they arrive a beat after their parent. */
export const lagged = (t: number, lag: number): number => Math.max(0, t - lag);

/** Eased, per-item overlapping entrance: item i starts `per` after item i-1. */
export function overlapIn(t: number, i: number, per = 0.09, dur = 0.5, bounce = 0.25): number {
  return settle(t - i * per, dur, bounce);
}

/* ── secondary action: idle life ───────────────────────────────────────────── */

/** Gentle breathing scale (≈±depth) — nothing alive is perfectly still. */
export function breathe(t: number, rate = 0.55, depth = 0.012): number {
  return 1 + Math.sin(TAU * rate * t) * depth;
}

/** Small idle sway in radians, phase-shifted by seed so a cast never syncs. */
export function sway(t: number, seed = 0, amp = 0.02, rate = 0.4): number {
  return Math.sin(TAU * rate * t + seed * 2.399) * amp;
}

/* ── timing accents ────────────────────────────────────────────────────────── */

/** A decaying accent that HITS at time `at` (scale/glow bump on a payoff). */
export function punch(t: number, at: number, strength = 0.08, decay = 5): number {
  const dt = t - at;
  if (dt <= 0) return 0;
  return strength * Math.exp(-decay * dt) * Math.sin(Math.min(Math.PI, dt * 14));
}

/** Cinematic crossfade ramp: incoming eases OUT (fast attack, soft landing). */
export function cutIn(p: number): number {
  return easeOutQuint(clamp01(p));
}

/** Outgoing side of a cut: lingers, then leaves (slow attack, fast release). */
export function cutOut(p: number): number {
  return 1 - easeInCubic(1 - clamp01(p));
}
